import React, {
  lazy,
  Suspense,
  startTransition,
  type ComponentType,
  type LazyExoticComponent,
} from 'react';
import { Route, Routes } from 'react-router';
import TrackerRoute from 'utils/analytics/TrackerRoute.react';
import AuthRoute from 'components/auth/AuthRoute.react';
import AdminRouteImport from 'components/admin/AdminRoute.react';
import { MaterialSpinner } from 'components/shared';
import CrashReporterErrorBoundary from 'utils/crash-reporting/CrashReporterErrorBoundary.react';
import AppLayout from 'components/app/Layout.react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

// Still-JS route modules expose incomplete prop types to tsc; widen until the
// components batch converts them.
type AnyProps = Record<string, unknown>;
type AnyLazy = LazyExoticComponent<ComponentType<AnyProps>>;
const asAnyLazy = (c: unknown): AnyLazy => {
  return c as AnyLazy;
};
const AdminRoute = AdminRouteImport as unknown as ComponentType<AnyProps>;

// Lazy loaded components with prefetching hints
const CompanyRoutes = asAnyLazy(
  lazy(() => {
    return import('components/company/CompanyRoutes.react');
  })
);
const ContentRoutes = asAnyLazy(
  lazy(() => {
    return import('components/content/ContentRoutes.react');
  })
);
const CalculatorsRoutes = asAnyLazy(
  lazy(() => {
    return import('components/calculators/CalculatorsRoutes.react');
  })
);
const AuthRoutes = asAnyLazy(
  lazy(() => {
    return import('components/auth/AuthRoutes.react');
  })
);
const PortfoliosRoutes = asAnyLazy(
  lazy(() => {
    return import('components/portfolios/PortfoliosRoutes.react');
  })
);
const LedgersRoutes = asAnyLazy(
  lazy(() => {
    return import('components/ledgers/LedgersRoutes.react');
  })
);
const AccountsRoutes = asAnyLazy(
  lazy(() => {
    return import('components/accounts/AccountsRoutes.react');
  })
);
const AdminRoutes = asAnyLazy(
  lazy(() => {
    return import('components/admin/AdminRoutes.react');
  })
);
const HomePage = asAnyLazy(
  lazy(() => {
    return import('components/home/HomePage.react');
  })
);
const ProfilePage = asAnyLazy(
  lazy(() => {
    return import('components/profile/ProfilePage.react');
  })
);
const PageNotFound = asAnyLazy(
  lazy(() => {
    return import('components/errors/PageNotFound.react');
  })
);

// Enhanced loading component that shows a progress indicator
const Loading = () => {
  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box
        sx={{ width: '100%', position: 'fixed', top: 0, left: 0, zIndex: 9999 }}
      >
        <LinearProgress />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <MaterialSpinner />
      </Box>
    </Box>
  );
};

// App.js calls `createRoutes(store)`; the store arg is unused (extra args are
// ignored at runtime). Hooks below run during the caller's render.
// Optional store arg kept for App.tsx call-site compatibility (unused).
export default function createRoutes(_store?: unknown) {
  // Prefetch high-priority routes
  React.useEffect(() => {
    // Prefetch main routes that are likely to be visited
    const prefetchPriorities = [
      import('components/home/HomePage.react'),
      import('components/portfolios/PortfoliosRoutes.react'),
      import('components/ledgers/LedgersRoutes.react'),
      import('components/auth/AuthRoutes.react'),
    ];

    // These prefetch calls won't block rendering
    startTransition(() => {
      prefetchPriorities.forEach((module) => {
        return module.catch(() => {});
      });
    });
  }, []);

  return (
    <CrashReporterErrorBoundary>
      <AppLayout>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<TrackerRoute element={<HomePage />} />} />
            <Route
              path="/profile"
              element={<AuthRoute element={<ProfilePage />} />}
            />
            <Route
              path="/company/*"
              element={<CompanyRoutes baseRoute={'/company'} />}
            />
            <Route
              path="/content/*"
              element={<ContentRoutes baseRoute={'/content'} />}
            />
            <Route
              path="/calculators/*"
              element={<CalculatorsRoutes baseRoute={'/calculators'} />}
            />
            <Route
              path="/auth/*"
              element={<AuthRoutes baseRoute={'/auth'} />}
            />
            <Route
              path="/portfolios/*"
              element={
                <AuthRoute
                  element={<PortfoliosRoutes baseRoute={'/portfolios'} />}
                />
              }
            />
            <Route
              path="/ledgers/*"
              element={
                <AuthRoute element={<LedgersRoutes baseRoute={'/ledgers'} />} />
              }
            />
            <Route
              path="/accounts/*"
              element={
                <AuthRoute
                  element={<AccountsRoutes baseRoute={'/accounts'} />}
                />
              }
            />
            <Route
              path="/admin/*"
              element={
                <AdminRoute element={<AdminRoutes />} baseRoute={'/admin'} />
              }
            />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Suspense>
      </AppLayout>
    </CrashReporterErrorBoundary>
  );
}
